/**
 * AuthProvider
 * Провайдер авторизации для всего приложения
 */

import { createContext, useEffect, useRef, useState, ReactNode } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { Profile, ThemeName } from '@/types/profile';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
  profileLoading: boolean;
  profileError: string | null;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithYandex: () => Promise<void>;
  verifyOtp: (email: string, token: string) => Promise<{ error: AuthError | null }>;
  resendOtp: (email: string) => Promise<{ error: AuthError | null }>;
  refreshProfile: () => Promise<void>;
  updateTheme: (theme: ThemeName) => Promise<void>;
  updateChapterFontSize: (fontSize: number) => Promise<void>;
  updateGalleryImageSettings: (model: string, aspectRatio: string) => Promise<void>;
  updateIdeaModel: (model: string) => Promise<void>;
  updateChaptersModel: (model: string) => Promise<void>;
  updateActiveBookId: (bookId: string | null) => Promise<void>;
  updateActiveChapterId: (chapterId: string | null) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

const MIN_CHAPTER_FONT_SIZE = 12;
const MAX_CHAPTER_FONT_SIZE = 20;

function normalizeChapterFontSize(fontSize: number) {
  return Math.min(MAX_CHAPTER_FONT_SIZE, Math.max(MIN_CHAPTER_FONT_SIZE, Math.round(fontSize)));
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const profileRef = useRef<Profile | null>(null);
  const currentUserIdRef = useRef<string | null>(null);

  const setProfileState = (nextProfileOrUpdater: Profile | null | ((prevProfile: Profile | null) => Profile | null)) => {
    setProfile((prevProfile) => {
      const nextProfile =
        typeof nextProfileOrUpdater === 'function'
          ? nextProfileOrUpdater(prevProfile)
          : nextProfileOrUpdater;

      profileRef.current = nextProfile;
      return nextProfile;
    });
  };

  // Функция очистки состояния. Объявлена до loadProfile/signOut, которые на неё ссылаются.
  const clearAuthState = () => {
    setUser(null);
    setSession(null);
    currentUserIdRef.current = null;
    setProfileState(null);
    setProfileError(null);
  };

  // Объявлена до loadProfile: тот вызывает signOut в setTimeout при невалидной сессии
  // (иначе immutability: "access before declared"). Поведение в рантайме не меняется.
  const signOut = async () => {
    await supabase.auth.signOut();
    clearAuthState();
  };

  // Функция загрузки профиля
  const loadProfile = async (userId: string) => {
    setProfileLoading(true);
    setProfileError(null);

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('Profile load error:', error);
        
        // Если профиль не найден (404) - это невалидная сессия
        if (error.code === 'PGRST116') {
          console.warn('Profile not found for user, signing out...');
          setProfileError('Сессия устарела. Пожалуйста, войдите снова.');
          // Выполняем выход через небольшую задержку, чтобы пользователь увидел сообщение
          setTimeout(() => {
            signOut();
          }, 2000);
        } else {
          setProfileError('Не удалось загрузить профиль пользователя');
        }
        setProfileState(null);
      } else {
        setProfileState(data);
      }
    } catch (err) {
      console.error('Profile load exception:', err);
      setProfileError('Произошла ошибка при загрузке профиля');
      setProfileState(null);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    // Проверяем текущую сессию при загрузке
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      currentUserIdRef.current = session?.user?.id ?? null;
      
      // Загружаем профиль если есть пользователь
      if (session?.user) {
        loadProfile(session.user.id);
      }
      
      setLoading(false);
    });

    // Подписываемся на изменения авторизации
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user ?? null;
      const nextUserId = nextUser?.id ?? null;
      const previousUserId = currentUserIdRef.current;

      setSession(session);
      setUser(nextUser);
      currentUserIdRef.current = nextUserId;
      
      // Загружаем профиль при входе
      if (nextUser) {
        const shouldLoadProfile = nextUserId !== previousUserId || !profileRef.current;
        if (shouldLoadProfile) {
          loadProfile(nextUser.id);
        }
      } else {
        // Очищаем профиль при выходе
        clearAuthState();
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
    // Подписка Supabase auth: эффект намеренно mount-only ([]). loadProfile/clearAuthState
    // не мемоизированы — внесение их в deps пере-подписывало бы onAuthStateChange каждый
    // рендер. Значения берутся актуальные на момент срабатывания колбэка.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error };
  };

  const signUp = async (email: string, password: string) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
    });
    return { error };
  };

  const signInWithGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin,
      },
    });
    
    if (error) {
      throw error;
    }
  };

  const signInWithYandex = async () => {
    // Яндекс OAuth пока не настроен
    throw new Error('Yandex OAuth is not enabled yet');
  };

  const verifyOtp = async (email: string, token: string) => {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'email',
    });
    return { error };
  };

  const resendOtp = async (email: string) => {
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });
    return { error };
  };

  const refreshProfile = async () => {
    if (user) {
      await loadProfile(user.id);
    }
  };

  const updateTheme = async (theme: ThemeName) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ theme })
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Theme update error:', error);
        return;
      }

      // Обновляем локальное состояние профиля
      if (profile) {
        setProfileState({ ...profile, theme });
      }

      // Сохраняем в localStorage для быстрого применения
      localStorage.setItem('user-theme', theme);
    } catch (err) {
      console.error('Theme update exception:', err);
    }
  };

  const updateChapterFontSize = async (fontSize: number) => {
    if (!user) return;

    const nextFontSize = normalizeChapterFontSize(fontSize);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ chapter_font_size: nextFontSize })
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Chapter font size update error:', error);
        return;
      }

      setProfileState(prevProfile => (
        prevProfile ? { ...prevProfile, chapter_font_size: nextFontSize } : prevProfile
      ));
    } catch (err) {
      console.error('Chapter font size update exception:', err);
    }
  };

  const updateGalleryImageSettings = async (model: string, aspectRatio: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          gallery_image_model: model,
          gallery_image_aspect_ratio: aspectRatio,
        })
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Gallery image settings update error:', error);
        return;
      }

      setProfileState(prevProfile => (
        prevProfile
          ? {
              ...prevProfile,
              gallery_image_model: model,
              gallery_image_aspect_ratio: aspectRatio,
            }
          : prevProfile
      ));
    } catch (err) {
      console.error('Gallery image settings update exception:', err);
    }
  };

  const updateIdeaModel = async (model: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ idea_model: model })
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Idea model update error:', error);
        return;
      }

      setProfileState(prevProfile => (
        prevProfile ? { ...prevProfile, idea_model: model } : prevProfile
      ));
    } catch (err) {
      console.error('Idea model update exception:', err);
    }
  };

  const updateChaptersModel = async (model: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ chapters_model: model })
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Chapters model update error:', error);
        return;
      }

      setProfileState(prevProfile => (
        prevProfile ? { ...prevProfile, chapters_model: model } : prevProfile
      ));
    } catch (err) {
      console.error('Chapters model update exception:', err);
    }
  };

  // Mirrors updateActiveBookId. Failures are logged and swallowed on purpose: remembering the
  // chapter is a convenience, and a build deployed before the T-G01 migration would otherwise throw
  // on every chapter switch — it simply keeps the old "first chapter" behaviour instead.
  const updateActiveChapterId = async (chapterId: string | null) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ active_chapter_id: chapterId })
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Active chapter update error:', error);
        return;
      }

      setProfileState(prevProfile => (
        prevProfile ? { ...prevProfile, active_chapter_id: chapterId } : prevProfile
      ));
    } catch (err) {
      console.error('Active chapter update exception:', err);
    }
  };

  const updateActiveBookId = async (bookId: string | null) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ active_book_id: bookId })
        .eq('id', user.id)
        .select();

      if (error) {
        console.error('Active book update error:', error);
        return;
      }

      setProfileState(prevProfile => (
        prevProfile ? { ...prevProfile, active_book_id: bookId } : prevProfile
      ));
    } catch (err) {
      console.error('Active book update exception:', err);
    }
  };

  const value = {
    user,
    session,
    profile,
    loading,
    profileLoading,
    profileError,
    signIn,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithYandex,
    verifyOtp,
    resendOtp,
    refreshProfile,
    updateTheme,
    updateChapterFontSize,
    updateGalleryImageSettings,
    updateIdeaModel,
    updateChaptersModel,
    updateActiveBookId,
    updateActiveChapterId,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
